import { Plus, Trash2 } from 'lucide-react';
import type { Glosario } from '@/api/installation.api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';

/**
 * Pestaña GLOSARIO — portada de silvai-chat.
 *
 * ⚠️ En conversia-core el glosario nace VACÍO: cada negocio carga el suyo. Y
 * todavía no hay asistente interno (copiloto) que lo use: la pantalla lo dice,
 * en vez de prometer un efecto que hoy no existe.
 *
 * Esto no es documentación: cada fila viaja dentro del prompt del copiloto. Es
 * la diferencia entre "el técnico ponchó" y "el empleado registró su
 * asistencia" —lo segundo lo delata como sistema en la primera frase—.
 *
 * La pantalla lo dice sin rodeos, porque el dueño tiene que entender que
 * escribir acá cambia cómo le habla el agente, no cómo se guarda un dato.
 */

export function GlosarioTab({
  value,
  onChange,
}: {
  value: Glosario;
  onChange: (patch: Partial<Glosario>) => void;
}) {
  const terminos = value.terminos ?? [];
  const evitar = value.evitar ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Cómo habla este negocio</CardTitle>
          <CardDescription>
            Las palabras que usan en este negocio y qué significan. Las va a usar el asistente
            interno para hablar como hablan ustedes. <strong>Todavía no está activo en esta
            instalación</strong>: lo que cargue queda guardado y listo para cuando llegue. No cambia
            cómo se guardan los datos.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {terminos.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              Este negocio todavía no tiene vocabulario propio. Sin él, el asistente habla
              genérico, que es justo lo que lo delata como programa. Agregue las palabras que se
              usan acá adentro.
            </p>
          )}

          {terminos.map((t, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-end"
            >
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">La palabra</span>
                <Input
                  value={t.termino}
                  placeholder="despachar"
                  onChange={(e) => {
                    const next = [...terminos];
                    next[i] = { ...t, termino: e.target.value };
                    onChange({ terminos: next });
                  }}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Qué significa acá adentro
                </span>
                <Input
                  value={t.significa}
                  placeholder="Sacar un pedido del depósito"
                  onChange={(e) => {
                    const next = [...terminos];
                    next[i] = { ...t, significa: e.target.value };
                    onChange({ terminos: next });
                  }}
                />
              </label>

              <Button
                variant="ghost"
                size="sm"
                aria-label={`Quitar ${t.termino || 'la palabra'}`}
                onClick={() => onChange({ terminos: terminos.filter((_, j) => j !== i) })}
              >
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </div>
          ))}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChange({ terminos: [...terminos, { termino: '', significa: '' }] })}
          >
            <Plus className="mr-1 h-4 w-4" /> Agregar palabra
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lo que no hay que decir</CardTitle>
          <CardDescription>
            Cada línea necesita el reemplazo: decir "no digas empleado" sin dar la alternativa no
            le sirve de nada al asistente. Las filas sin reemplazo se descartan al guardar.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {evitar.map((e, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
            >
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">En vez de…</span>
                <Input
                  value={e.no}
                  placeholder="el usuario final"
                  onChange={(ev) => {
                    const next = [...evitar];
                    next[i] = { ...e, no: ev.target.value };
                    onChange({ evitar: next });
                  }}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">…decí</span>
                <Input
                  value={e.si}
                  placeholder="el cliente"
                  onChange={(ev) => {
                    const next = [...evitar];
                    next[i] = { ...e, si: ev.target.value };
                    onChange({ evitar: next });
                  }}
                />
              </label>

              <Button
                variant="ghost"
                size="sm"
                aria-label={`Quitar la regla de ${e.no || 'esta palabra'}`}
                onClick={() => onChange({ evitar: evitar.filter((_, j) => j !== i) })}
              >
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </div>
          ))}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChange({ evitar: [...evitar, { no: '', si: '' }] })}
          >
            <Plus className="mr-1 h-4 w-4" /> Agregar regla
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
